// frontend/hooks/useSidebarActions.ts

import { useRef } from "react";
import { API_URL, authHeaders, authOnlyHeaders, extractApiError } from "../utils/api";
import { addToDeadLinks, generateDocId, UserSession } from "../utils/documentUtils";
import { copyToClipboard } from "../utils/clipboard";
import { writeLocalStorage } from "./useLocalStorage";
import { setFavorite } from "../utils/favorites";

/**
 * 사이드바 컨텍스트 메뉴·모달에서 발생하는 문서 CRUD 액션 모음.
 * useSidebarLogic에서 상태(state)를 주입받아 사용합니다.
 */
export function useSidebarActions({
  user, router, pathname, documents, setDocuments, fetchDocuments, setExpandedDocs,
  createModal, setCreateModal, renameModal, setRenameModal, deleteModal, setDeleteModal,
  contextMenu, setContextMenu,
}: {
  user: UserSession;
  router: any;
  pathname: string;
  documents: any[];
  setDocuments: (fn: (prev: any[]) => any[]) => void;
  fetchDocuments: () => void;
  setExpandedDocs: (fn: (prev: any) => any) => void;
  createModal: any;
  setCreateModal: (v: any) => void;
  renameModal: any;
  setRenameModal: (fn: (prev: any) => any) => void;
  deleteModal: any;
  setDeleteModal: (v: any) => void;
  contextMenu: any;
  setContextMenu: (fn: (prev: any) => any) => void;
}) {
  // 중복 제출 방지 플래그 (폼·버튼 연타 방어)
  const isSubmitting = useRef(false);

  /** 새 문서 생성 요청을 처리합니다. 생성 후 해당 문서 페이지로 이동합니다. */
  const submitCreateDocument = async () => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;

    const { type, parentId, title, templateId } = createModal;
    // 빈 제목이면 빈 문자열 그대로 저장 — "새 페이지" 표시는 렌더링 시점의 폴백으로만 처리
    const finalTitle = title.trim();
    const newId = generateDocId();
    const targetDept = type === "TEAM" ? user.department : null;

    try {
      const res = await fetch(`${API_URL}/documents`, {
        method: "POST",
        headers: authHeaders(user?.accessToken),
        body: JSON.stringify({
          id: newId,
          title: finalTitle,
          workspaceType: type,
          ownerId: user.id,
          accessDept: targetDept,
          isGuestAllowed: false,
          requesterRole: user.role,
          requesterDept: user.department,
          parentId,
          userName: user?.name,
          // 템플릿을 골랐으면 그 문서를 원본으로 복제한다.
          // 서버의 기존 복제 경로(sourceDocId)를 그대로 태우므로 본문·하위 문서·아이콘이 함께 온다.
          ...(templateId ? { sourceDocId: templateId } : {}),
        }),
      });

      if (res.ok) {
        if (parentId) {
          setExpandedDocs((prev: any) => ({ ...prev, [parentId]: true }));
          window.dispatchEvent(
            new CustomEvent("insert-child-block", { detail: { parentId, childId: newId, childTitle: finalTitle } }),
          );
        }
        fetchDocuments();
        setCreateModal({ visible: false, type: "PERSONAL", parentId: null, parentTitle: "", title: "", templateId: null });
        router.push(`/doc/${newId}`);
      } else {
        alert(await extractApiError(res, "문서 생성에 실패했습니다."));
      }
    } catch {
      alert("서버와 통신하는 중 네트워크 오류가 발생했습니다.");
    } finally {
      isSubmitting.current = false;
    }
  };

  /** 문서 이름과 아이콘을 서버에 저장하고, 관련 이벤트를 발행합니다. */
  const submitRename = async () => {
    if (!renameModal.title.trim()) return;
    try {
      const res = await fetch(`${API_URL}/documents/${renameModal.docId}`, {
        method: "PATCH",
        headers: authHeaders(user?.accessToken),
        body: JSON.stringify({ title: renameModal.title, icon: renameModal.icon }),
      });

      if (res.ok) {
        window.dispatchEvent(
          new CustomEvent("doc-meta-updated", {
            detail: { docId: renameModal.docId, title: renameModal.title, icon: renameModal.icon },
          }),
        );
        fetchDocuments();
        window.dispatchEvent(new Event("document-title-updated"));
        window.dispatchEvent(new Event("document-updated"));
        window.dispatchEvent(new Event("force-sidebar-refresh"));
        setRenameModal((prev: any) => ({ ...prev, visible: false, docId: "", title: "", x: 0, y: 0, icon: null }));
      } else {
        alert(await extractApiError(res, "문서 이름 변경에 실패했습니다."));
      }
    } catch {
      alert("서버와 통신하는 중 네트워크 오류가 발생했습니다.");
    }
  };

  /** 문서를 휴지통으로 이동합니다. 현재 문서 페이지에 있으면 홈으로 이동합니다. */
  const submitDelete = async () => {
    try {
      const targetDocId = deleteModal.docId;
      const res = await fetch(`${API_URL}/documents/${targetDocId}`, {
        method: "DELETE",
        headers: authOnlyHeaders(user?.accessToken),
      });

      if (res.ok) {
        addToDeadLinks(targetDocId);
        window.dispatchEvent(new Event("force-sidebar-refresh"));
        fetchDocuments();
        if (pathname === `/doc/${targetDocId}`) router.push("/");
        setDeleteModal({ visible: false, docId: "", title: "" });

        window.dispatchEvent(new CustomEvent("show-toast", {
          detail: {
            message: "휴지통으로 이동됨",
            action: {
              label: "복원하기",
              onClick: async () => {
                await fetch(`${API_URL}/documents/${targetDocId}/restore`, {
                  method: "PATCH",
                  headers: authHeaders(user?.accessToken),
                  body: JSON.stringify({ requesterId: user?.id, requesterRole: user?.role, requesterDept: user?.department || "" }),
                });
                fetchDocuments();
                window.dispatchEvent(new Event("force-sidebar-refresh"));
              },
            },
          },
        }));
      } else {
        alert(await extractApiError(res, "문서 삭제에 실패했습니다."));
      }
    } catch {
      alert("서버와 통신하는 중 네트워크 오류가 발생했습니다.");
    }
  };

  /**
   * 사이드바 컨텍스트 메뉴에서 문서를 복제합니다.
   * 낙관적 UI 업데이트로 목록에 즉시 표시한 뒤 서버에 요청하고, 실패 시 롤백합니다.
   */
  const handleMenuDuplicate = async () => {
    if (isSubmitting.current || !contextMenu.docId) return;
    const original = documents.find((d: any) => d.id === contextMenu.docId);
    if (!original) return;

    // 이미 "(N)" 형태 접미사가 있으면 숫자를 증가시키고, 없으면 "(1)"을 추가
    let duplicateTitle = `${original.title || "새 페이지"} (1)`;
    const match = original.title.match(/(.*)\s\((\d+)\)$/);
    if (match) duplicateTitle = `${match[1]} (${parseInt(match[2], 10) + 1})`;

    isSubmitting.current = true;
    const newId = generateDocId();

    setContextMenu((prev: any) => ({ ...prev, visible: false }));

    // 낙관적 업데이트: 원본 문서 바로 아래에 즉시 표시
    const now = new Date().toISOString();
    setDocuments((prev: any) => {
      const newDoc = {
        id: newId,
        title: duplicateTitle,
        workspaceType: original.workspaceType,
        parentId: original.parentId,
        isFavorite: false,
        viewedAt: now,
        accessDept: original.accessDept,
        ownerId: original.ownerId,
        icon: "transparent",
        coverImage: null,
      };
      const idx = prev.findIndex((d: any) => d.id === original.id);
      if (idx !== -1) {
        const next = [...prev];
        next.splice(idx + 1, 0, newDoc);
        return next;
      }
      return [...prev, newDoc];
    });

    if (original.parentId) {
      setExpandedDocs((prev: any) => ({ ...prev, [original.parentId]: true }));
    }

    try {
      const res = await fetch(`${API_URL}/documents`, {
        method: "POST",
        headers: authHeaders(user?.accessToken),
        body: JSON.stringify({
          id: newId,
          title: duplicateTitle,
          workspaceType: original.workspaceType,
          ownerId: original.ownerId,
          accessDept: original.accessDept,
          isGuestAllowed: original.isGuestAllowed,
          requesterRole: user.role,
          requesterDept: user.department,
          parentId: original.parentId,
          userName: user?.name,
          sourceDocId: original.id,
        }),
      });

      if (res.ok) {
        fetchDocuments();
        router.push(`/doc/${newId}`);
      } else {
        // 서버 실패 시 낙관적 업데이트 롤백
        setDocuments((prev: any) => prev.filter((d: any) => d.id !== newId));
        alert(await extractApiError(res, "문서 복제에 실패했습니다."));
      }
    } catch {
      setDocuments((prev: any) => prev.filter((d: any) => d.id !== newId));
      alert("서버와 통신하는 중 네트워크 오류가 발생했습니다.");
    } finally {
      setContextMenu((prev: any) => ({ ...prev, visible: false }));
      isSubmitting.current = false;
    }
  };

  /**
   * 즐겨찾기 상태를 토글합니다.
   * 정본이 서버(Favorite 테이블)로 옮겨졌다 — 이전에는 localStorage 에만 저장돼
   * 다른 PC·다른 브라우저에서 즐겨찾기가 사라졌다. UI 는 낙관적으로 먼저 바꾸고, 서버 반영이
   * 실패하면 되돌린다.
   */
  const handleToggleFavorite = async () => {
    if (!contextMenu.docId) return;
    const targetId = contextMenu.docId;
    const newFavoriteStatus = !contextMenu.isFavorite;

    setContextMenu((prev: any) => ({ ...prev, visible: false }));

    const applyLocal = (value: boolean) => {
      setDocuments((prev: any) =>
        prev.map((doc: any) => (doc.id === targetId ? { ...doc, isFavorite: value } : doc)),
      );
      // 문서 페이지 상단바 즐겨찾기 버튼과 동기화
      window.dispatchEvent(
        new CustomEvent("doc-favorite-changed", { detail: { docId: targetId, isFavorite: value } }),
      );
    };

    applyLocal(newFavoriteStatus);

    const ok = await setFavorite(targetId, newFavoriteStatus, user?.id, user?.accessToken);
    if (!ok) {
      applyLocal(!newFavoriteStatus); // 서버 반영 실패 — 화면을 되돌려 서버와 어긋나지 않게 한다
      alert("즐겨찾기를 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.");
    }
  };

  /** 문서를 최근 목록에서 제거합니다 (viewedAt → null). */
  const handleRemoveFromRecent = async () => {
    if (!contextMenu.docId) return;
    const targetId = contextMenu.docId;

    setContextMenu((prev: any) => ({ ...prev, visible: false }));
    setDocuments((prev: any) =>
      prev.map((doc: any) => (doc.id === targetId ? { ...doc, viewedAt: null } : doc)),
    );

    try {
      const res = await fetch(`${API_URL}/documents/${targetId}/recent`, {
        method: "DELETE",
        headers: authOnlyHeaders(user?.accessToken),
      });
      if (!res.ok) {
        fetchDocuments();
        alert("최근 문서에서 제거하는 데 실패했습니다.");
      }
    } catch {
      fetchDocuments();
      alert("서버와 통신하는 중 오류가 발생했습니다.");
    }
  };

  /**
   * 본인이 공유받은 문서에 대한 접근을 스스로 종료합니다.
   * 성공하면 어떤 목록에도 더 이상 보이면 안 되므로 documents에서 완전히 제거합니다.
   */
  const handleEndShare = async () => {
    if (!contextMenu.docId) return;
    const targetId = contextMenu.docId;

    setContextMenu((prev: any) => ({ ...prev, visible: false }));
    if (!confirm("공유를 종료하시겠습니까? 공유를 종료하면 앞으로 해당 문서와 하위 문서를 조회/수정할 수 없습니다.")) return;

    try {
      const res = await fetch(`${API_URL}/documents/${targetId}/share`, {
        method: "DELETE",
        headers: authOnlyHeaders(user?.accessToken),
      });
      if (res.ok) {
        setDocuments((prev: any) => prev.filter((doc: any) => doc.id !== targetId));
        window.dispatchEvent(new Event("force-sidebar-refresh"));
        if (pathname === `/doc/${targetId}`) router.push("/");
      } else {
        alert(await extractApiError(res, "공유 종료에 실패했습니다."));
      }
    } catch {
      alert("서버와 통신하는 중 오류가 발생했습니다.");
    }
  };

  /** 현재 문서 URL을 클립보드에 복사합니다. */
  const handleCopyLink = async () => {
    if (!contextMenu.docId) return;
    const docUrl = `${window.location.origin}/doc/${contextMenu.docId}`;

    const ok = await copyToClipboard(docUrl);
    if (ok) {
      alert("링크가 클립보드에 복사되었습니다.");
    } else {
      alert("클립보드 복사에 실패했습니다. 브라우저 권한을 확인해 주세요.");
    }

    setContextMenu((prev: any) => ({ ...prev, visible: false }));
  };

  /** 문서를 다른 위치(부모·워크스페이스)로 이동합니다. */
  const handleMoveDocument = async (
    docId: string,
    targetParentId: string | null,
    targetWorkspaceType: string,
  ) => {
    const movedDoc = documents.find((d: any) => d.id === docId);
    const parentDoc = targetParentId ? documents.find((d: any) => d.id === targetParentId) : null;
    const locationLabel = parentDoc?.title || (targetWorkspaceType === "PERSONAL" ? "개인 페이지" : "팀 스페이스");

    try {
      const res = await fetch(`${API_URL}/documents/${docId}`, {
        method: "PATCH",
        headers: authHeaders(user?.accessToken),
        body: JSON.stringify({
          parentId: targetParentId,
          workspaceType: targetWorkspaceType,
          requesterRole: user.role,
          requesterDept: user.department,
          requesterId: user.id,
          userName: user.name,
        }),
      });
      if (res.ok) {
        fetchDocuments();
        window.dispatchEvent(new Event("force-sidebar-refresh"));

        window.dispatchEvent(new CustomEvent("show-toast", {
          detail: {
            message: `${movedDoc?.title || "문서"}의 변경된 위치는 ${locationLabel}입니다.`,
            action: targetParentId
              ? { label: "이동", onClick: () => router.push(`/doc/${targetParentId}`) }
              : undefined,
          },
        }));
      } else {
        alert(await extractApiError(res, "이동에 실패했습니다."));
      }
    } catch {
      alert("서버와 통신하는 중 네트워크 오류가 발생했습니다.");
    }
  };

  return {
    submitCreateDocument,
    submitRename,
    submitDelete,
    handleMenuDuplicate,
    handleToggleFavorite,
    handleRemoveFromRecent,
    handleEndShare,
    handleCopyLink,
    handleMoveDocument,
  };
}
